import React, { useEffect, useRef, useState } from "react"
import { MdOpenInNew } from "react-icons/md"

import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { countryData, CountryType } from "@letshang/assets"
import CountriesFlag from "@letshang/assets/countries_flags.json"
import { TWebUser } from "@letshang/constants"
import {
  formatPhoneNumber,
  getPhoneNumberPlaceholder,
  isPhoneNumberValid,
} from "@letshang/utils"
import * as Sentry from "@sentry/react"
import axios from "axios"
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import { size } from "lodash-es"
import { useRecoilState } from "recoil"

import { screenState, userState } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import {
  DownloadLinkMap,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

import { recaptchaErrorhandler, useRecaptcha } from "../RecaptchaStateProvider"

const Phone = ({ reInitializeTheRecaptchaVerifier }) => {
  const device = useDeviceDetection()
  const downloadLink = DownloadLinkMap[device]
  const phoneUtil = PhoneNumberUtil.getInstance()

  const [step, setStep] = useRecoilState(screenState)
  const [, setUser] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)
  const [country, setCountry] = useState<CountryType>(countryData[4])
  const [phoneNumberPlaceHolder, setPhoneNumberPlaceHolder] = useState({
    placeHolder: "",
    fullNumber: 0,
  })

  const [phone, setPhone] = useState<string>("")
  const [phoneFormatted, setPhoneFormatted] = useState<string>("")
  const [phoneDisplayed, setPhoneDisplayed] = useState<string>("")

  const { verifier, recaptchaVerifiedState, isProcessing } = useRecaptcha()

  // useEffect(() => {
  //   let recaptchaIframe = null
  //   let parentRecaptchaContainerDiv = null
  //   let reCaptchaChallengeClosureObserver = null

  //   const mainObserver = new MutationObserver((mutations) => {
  //     for (const mutation of mutations) {
  //       if (mutation.addedNodes.length) {
  //         parentRecaptchaContainerDiv = mutation.addedNodes[0]

  //         recaptchaIframe = parentRecaptchaContainerDiv.querySelectorAll(
  //           'iframe[title*="recaptcha challenge"]',
  //         )

  //         if (recaptchaIframe.length) {
  //           reCaptchaChallengeClosureObserver = new MutationObserver(
  //             (mutationsAtSecondLevel) => {
  //               for (const mutation of mutationsAtSecondLevel) {
  //                 if (
  //                   mutation.type === "attributes" &&
  //                   mutation.attributeName === "style"
  //                 ) {
  //                   const target = mutation.target as HTMLElement
  //                   const visibility =
  //                     window.getComputedStyle(target).visibility

  //                   if (visibility === "visible") {
  //                     //no operation for now
  //                     // console.log(
  //                     //   "111 🟢 reCAPTCHA container is now visible (challenge opened)",
  //                     // )
  //                   } else if (visibility === "hidden") {
  //                     // console.log(
  //                     //   "222 🔴 reCAPTCHA container is now hidden (challenge closed/dismissed)",
  //                     // )
  //                     // in case of close or dismiss -> setLoading to false
  //                     setIsLoading(false)
  //                   }
  //                 }
  //               }
  //             },
  //           )

  //           reCaptchaChallengeClosureObserver.observe(
  //             parentRecaptchaContainerDiv,
  //             {
  //               attributes: true,
  //               attributeFilter: ["style"],
  //             },
  //           )
  //         }
  //       }
  //     }
  //   })

  //   console.log("111 document.body", document.body)
  //   mainObserver.observe(document.body, {
  //     childList: true,
  //     subtree: true,
  //     attributes: false,
  //     attributeOldValue: false,
  //     characterData: false,
  //     characterDataOldValue: false,
  //   })

  //   return () => {
  //     mainObserver && mainObserver.disconnect()
  //     reCaptchaChallengeClosureObserver &&
  //       reCaptchaChallengeClosureObserver.disconnect()
  //   }
  // }, [])

  async function signIn(phone: string) {
    if (isProcessing.current) {
      return
    }

    const formatted = formatPhoneNumber(phone, country.code)

    if (!isPhoneNumberValid(phone, country.code)) {
      return showErrorToast("Invalid phone number")
    }

    try {
      isProcessing.current = true
      setIsLoading(true)
      if (!recaptchaVerifiedState?.current) {
        // here we can add logic to re-initialize the instance and verify it from new verifier
        const wait = await new Promise<{
          isReloaded: boolean
          message?: string
        }>((resolve, reject) => {
          showErrorToast("Your verification expired.", {
            text2: "Please reload the page to sign in again.",
            buttonText: "Reload Page",
            onClose: () => {
              reject({
                isReloaded: false,
                message: "The user denied to re-authenticate the re-captcha",
                code: "auth/recaptcha-expired/re-auth-denied",
              })
            },
            onButtonClick: () => {
              reInitializeTheRecaptchaVerifier()
              if (verifier?.current) {
                verifier?.current.render() // to render the re-captcha again
                resolve({
                  isReloaded: true,
                })
              } else {
                reject({
                  isReloaded: false,
                  code: "auth/recaptcha-expired/verifier-not-available",
                  message:
                    "Verifier instance is not available even after re-initialization",
                })
              }
            },
          })
        })

        if (!wait?.isReloaded) {
          return
        }
      }

      const confirmation = await signInWithPhoneNumber(
        auth,
        country.dial_code + formatted.replace(/-/g, ""),
        verifier.current as RecaptchaVerifier,
      )
      setUser((prev: TWebUser) => ({
        ...prev,
        phone: phone,
        dial_code: country?.dial_code,
        verificationId: confirmation?.verificationId,
        country: country?.code,
      }))
      setStep(2)
    } catch (error: any) {
      console.error(error)
      Sentry.captureException({
        type: "exception",
        comp: "signup/phone",
        error: error,
      })

      Sentry.captureMessage(
        JSON.stringify({
          type: "message",
          error,
        }),
      )
      recaptchaErrorhandler(error)
    } finally {
      setIsLoading(false)
      isProcessing.current = false
    }
  }

  const [isSelectOpen, setIsSelectOpen] = React.useState(false)

  const getCurrentCountry = async () => {
    try {
      // wrote a util function, but added here for a quick fix, when called from util function it was not working giving cors error
      const country = await axios.get("https://ipapi.co/json/")

      if (country.data) {
        const data = country.data
        setCountry(
          (countryData.find((item) => item.code === data.country_code) ||
            countryData[5]) as CountryType,
        )
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    getCurrentCountry()
  }, [])

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Doing this cause recaptcha was removing focus from input field
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef])

  const [isInputFocused, setIsInputFocused] = useState(false)

  const getFormattedPhone = (digits: number | string) => {
    try {
      const inputNumber = String(digits || "").replace(/[^\d+]/g, "")
      if (!inputNumber) {
        console.error("Input number is empty or invalid")
        setPhoneDisplayed(String(digits || ""))
        return
      }

      // Validate the country code
      if (!country || !country.code) {
        console.error("Country code is missing or invalid")
        setPhoneDisplayed(String(digits || ""))
        return
      }

      // Parse the number
      const parsedNumber = phoneUtil.parse(inputNumber, country.code)

      // Format the number in NATIONAL format
      let formattedDisplay = phoneUtil.format(
        parsedNumber,
        PhoneNumberFormat.NATIONAL,
      )

      // Remove leading '0' if present
      if (formattedDisplay.startsWith("0")) {
        formattedDisplay = formattedDisplay.replace(/^0/, "")
      }

      // Set the displayed number
      setPhoneDisplayed(formattedDisplay)
    } catch (error) {
      // console.error("Error formatting phone number:", error.message);
      // setPhoneDisplayed("Invalid phone number");
      setPhoneDisplayed(String(digits || ""))
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const digits = e.target.value
    // Check if last entered character is a number
    const lastChar = digits.slice(-1)
    if (lastChar && !/^\d$/.test(lastChar)) {
      return
    }
    setPhone(digits)
    const formatted = formatPhoneNumber(digits, country.code)
    const raw = formatted
      .replace("-", "")
      .replace("(", "")
      .replace(")", "")
      .replace(" ", "")
    setPhoneFormatted(raw)

    getFormattedPhone(digits)
  }

  useEffect(() => {
    setPhoneNumberPlaceHolder(getPhoneNumberPlaceholder(country?.code) as any)
  }, [country?.code])

  const isButtonEnable = size(phoneDisplayed) >= 10

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full">
      <div className="w-[90%] max-w-sm mb-5 md:mb-10 text-start  ">
        <div>
          <p className="text-white/30 text-left text-sm mt-10 md:mt-0 lg:mt-0 font-medium">
            STEP 1
          </p>
          <FancyText className="text-xl md:text-2.5xl mt-[10px] text-white font-semibold">
            Signing you up
          </FancyText>
        </div>
      </div>
      <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
      <div className="w-[90%] max-w-sm bg-white/10 relative rounded-2xl p-10 space-y-6">
        {/* Title */}
        <div className="text-left">
          <h2 className="text-white text-2xl font-semibold">
            What's your phone number?
          </h2>
          <p className="text-white/35 mt-2">Please enter your phone number</p>
        </div>

        {/* Input field */}
        <div className="flex items-center gap-2 pb-24">
          {/* Country Flag */}
          <div
            className="flex items-center justify-center w-14 h-12 bg-white/20 rounded-md cursor-pointer"
            onClick={() => setIsSelectOpen(!isSelectOpen)}
          >
            <img
              alt="Country Flag"
              className="w-6 h-6 self-center"
              src={
                CountriesFlag[
                  country.code?.toLocaleLowerCase() as keyof typeof CountriesFlag
                ]
              }
            />
          </div>
          {/* Phone Input */}
          <div className="px-4 bg-white/20 h-12 rounded-md flex w-full items-center">
            <p className="text-white text-[15px]">{country?.dial_code}</p>

            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              placeholder={phoneNumberPlaceHolder?.placeHolder}
              className="w-full h-full bg-transparent text-white text-[16px] placeholder-white/35 outline-none px-2"
              readOnly={step !== 1}
              autoFocus
              maxLength={size(phoneNumberPlaceHolder?.placeHolder)}
              value={phoneDisplayed}
              onChange={handlePhoneChange}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
            />
          </div>
        </div>

        {isSelectOpen && (
          <div className="absolute bottom-0 left-10 w-[80%] bg-white/20 border-[1px] border-white/10 shadow-md p-[5px] rounded-[5px] cursor-pointer z-10 max-h-[200px] overflow-y-scroll">
            {countryData.map((item) => (
              <div
                className={
                  "flex items-center p-[5px] border-b-[1px] border-white/10 gap-[5px] text-white" +
                  (item.code === country.code ? " bg-[#0000001A]" : " ")
                }
                onClick={() => {
                  setCountry(item)
                  setIsSelectOpen(false)
                }}
              >
                <img
                  src={
                    CountriesFlag[
                      item.code?.toLocaleLowerCase() as keyof typeof CountriesFlag
                    ]
                  }
                  alt="india"
                  className="w-[20px] h-[20px] md:w-[30px] md:h-[30px]"
                />
                {item.dial_code}
              </div>
            ))}
          </div>
        )}

        {/* Continue Button */}
        <button
          className={`w-full bg-white/20 text-white py-3 rounded-full font-medium transition ${isButtonEnable ? "" : "opacity-50"}`}
          onClick={() => {
            // Prevent multiple clicks
            if (isProcessing.current || isLoading) {
              return
            }

            verifier?.current
              .verify()
              .then((_verifierObj) => {
                setTimeout(() => signIn(phoneFormatted), 500)
              })
              .catch((err) => {
                Sentry.captureMessage(
                  JSON.stringify({
                    type: "error",
                    comp: "signup/phone",
                    error: err,
                  }),
                )
                console.error("ERROR:", err)
                showErrorToast(
                  "Error occured while verifying recaptcha. Please try again later",
                )
              })
          }}
          disabled={!isButtonEnable || isProcessing.current || isLoading}
        >
          Continue
        </button>
      </div>

      <p className="text-white mt-10">Getting tired of constant logouts? </p>
      <div
        className="flex flex-row items-center cursor-pointer"
        onClick={() => onOpenDownloadLink(downloadLink)}
      >
        <p className="text-white">Use the app!</p>
        <MdOpenInNew className="text-white ml-1" />
      </div>

      <div className="absolute bottom-4">
        <p className="text-white/35">by signing up you agree to let's hang</p>
        <a
          href="https://pages.flycricket.io/walkme/terms.html"
          className="text-white/35 hover:text-white underline"
        >
          Terms and condition
        </a>
      </div>
    </div>
  )
}

export default Phone
